import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useAuth } from 'components/AuthProvider'
import { usePayments } from 'components/PaymentsProvider'
import { FormEvent, useMemo } from 'react'
import useStyles from './styles'
import { useMutateVerifyStripeTransaction } from 'hooks/useTransfers'
import { useWallet } from 'hooks/useWallet'
import { PaymentsResponse } from 'api/payments/types'

interface CreditOrDebitCardProps {
  handlePaymentSuccess?: () => void
  setDisabledButton: (disabled: boolean) => void
}

interface CreditOrDebitCheckoutProps extends CreditOrDebitCardProps {
  payment: PaymentsResponse
}

const CreditOrDebitCheckout = ({
  handlePaymentSuccess,
  setDisabledButton,
  payment
}: CreditOrDebitCheckoutProps): JSX.Element => {
  const { classes } = useStyles()

  const stripe = useStripe()
  const elements = useElements()

  const { review_data, payment_intent } = payment ?? {}

  const { setToast } = useAuth()

  const { mutateAsync: verifyStripeTransaction } =
    useMutateVerifyStripeTransaction()

  const { refetch: refetchWallet } = useWallet()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setDisabledButton(true)

    if (!stripe) return
    if (!elements) return

    const { data: walletData } = await refetchWallet()
    const balance = walletData?.balance ?? 0

    if (Number(balance) < Number(review_data.wallet_use_amount)) {
      return setToast({
        message: 'Your wallet balance has changed, please try again',
        open: true,
        type: 'error',
        duration: 3000
      })
    }

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required'
    })

    if (error) {
      setToast({
        message: error.message as string,
        open: true,
        type: 'error',
        duration: 3000
      })
      setDisabledButton(false)
    } else {
      const result = await verifyStripeTransaction(payment_intent?.id as string)

      if (result?.success) {
        if (handlePaymentSuccess) {
          handlePaymentSuccess()
        }
        setDisabledButton(true) // In the case that we don't just want to redirect, we probably want to reset the button back to disabled.
      }
    }
  }

  return (
    <form
      className={classes.form}
      id='credit-or-debit-form'
      data-testid='credit-or-debit-form'
      onSubmit={handleSubmit}
    >
      <PaymentElement id='credit-or-debit-form-payment-element' />
    </form>
  )
}

export default function CreditOrDebitCard(
  props: CreditOrDebitCardProps
): JSX.Element | null {
  const stripePromise = useMemo(
    () => loadStripe(process.env.REACT_APP_STRIPE_CLIENT_PUBLIC_KEY ?? ''),
    []
  )

  const { payment } = usePayments()

  if (payment === undefined) {
    return null
  }

  return (
    // @ts-ignore -- Elements type compatibility with Stripe Promise
    <Elements
      stripe={stripePromise}
      options={{ clientSecret: payment.payment_intent?.client_secret ?? '' }}
    >
      <CreditOrDebitCheckout {...props} payment={payment} />
    </Elements>
  )
}
